"use client"
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ExternalLink, Github, FileText } from "lucide-react"
import { projects } from "@/lib/data"
import { motion } from "framer-motion"
import Link from "next/link"

export default function ProjectsSection() {
  return (
    <section id="projects" className="section-padding">
      <div className="container">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="heading-responsive font-bold text-center mb-12 font-display"
        >
          Live Projects
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
              className="group"
            >
              <Card className="h-full flex flex-col overflow-hidden border-2 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 hover:border-primary/20 dark:hover:border-primary/30">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-3">
                    {project.icon && (
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent/20 group-hover:text-accent transition-colors duration-300">
                        {(() => {
                          const Icon = project.icon;
                          return <Icon className="h-5 w-5" />;
                        })()}
                      </div>
                    )}
                    <CardTitle className="text-xl font-semibold font-display tracking-tight">{project.title}</CardTitle>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground min-h-[60px] leading-relaxed">
                    {project.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow pb-4">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">Tech</h3>
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech) => (
                        <Badge 
                          key={tech} 
                          variant="secondary"
                          className="px-2.5 py-0.5 text-xs font-medium rounded-md"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-wrap gap-2 border-t pt-4">
                  {project.status === "live" && (
                    <Button variant="default" size="sm" className="h-11 md:h-9 font-medium shadow-sm" asChild>
                      <Link href={`/project/${project.slug}`} className="inline-flex items-center">
                        <ExternalLink className="mr-1.5 h-4 w-4" /> Live
                      </Link>
                    </Button>
                  )}
                  {"codeLink" in project && project.codeLink && (
                    <Button variant="outline" size="sm" className="h-11 md:h-9 font-medium" asChild>
                      <a href={project.codeLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                        <Github className="mr-1.5 h-4 w-4" /> Code
                      </a>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
